import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Heading, WarningCircleIcon } from '../index'

import { cn } from '@/lib/utils'

const alertVariants = cva('flex w-full rounded-sm p-xs gap-xs items-center', {
  variants: {
    variant: {
      default: 'bg-primary-container text-on-primary-container',
      destructive: 'border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
})

type AlertProps = {
  icon?: React.ReactElement
  text: string
}

const AlertWrapper = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
))
AlertWrapper.displayName = 'Alert'

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => <Heading size="sm" ref={ref} className={cn('mb-1', className)} {...props} />
)
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />
  )
)
AlertDescription.displayName = 'AlertDescription'

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(({ text, icon }, ref) => {
  return (
    <AlertWrapper ref={ref}>
      <div>{icon ? icon : <WarningCircleIcon className="fill-on-primary-container" />}</div>
      <AlertDescription>{text}</AlertDescription>
    </AlertWrapper>
  )
})

export { Alert }
